import React, { useState, useMemo, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ChevronLeft, ChevronRight, Plus, CalendarDays, ListChecks,
  Clock, User, AlertCircle, Filter, Loader2, Check, X, Search,
  Phone, MessageCircle, Video, Mail, ClipboardList,
} from 'lucide-react';
import { format, addDays, subDays, isToday, isBefore, isAfter, startOfDay, isSameDay } from 'date-fns';
import { safeFormat } from '@/lib/safeDate';
import { ptBR } from 'date-fns/locale';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { SearchBar } from '@/components/common/SearchBar';
import { ResponsiveDialog } from '@/components/ui/responsive-dialog';
import { MultiSelect } from '@/components/ui/multi-select';
import { CalendarGrid } from '@/components/agenda/CalendarGrid';
import { DayView } from '@/components/agenda/DayView';
import { EventFormModal } from '@/components/agenda/EventFormModal';
import { TaskFormModal } from '@/components/agenda/TaskFormModal';
import { TaskDetailSheet } from '@/components/tasks/TaskDetailSheet';
import { CallDetailSheet } from '@/components/agenda/CallDetailSheet';
import { EscalationTaskCompletionModal } from '@/components/tasks/EscalationTaskCompletionModal';
import { useAgendaEvents } from '@/hooks/useAgendaEvents';
import type { AgendaItem } from '@/hooks/useAgendaEvents';
import type { CalendarEvent } from '@/hooks/useCalendarEvents';
import { useCurrentAppUser } from '@/hooks/useCurrentAppUser';
import { useActiveAppUsersForSelect } from '@/hooks/useAppUsers';
import { useFilteredTasks } from '@/hooks/useFilteredTasks';
import { useEscalationContacts } from '@/hooks/useEscalationContacts';
import { useEscalationTasksBySearch } from '@/hooks/useEscalationTasksBySearch';
import { useTasksByClient } from '@/hooks/useTasksByClient';
import { useAppUsers } from '@/hooks/useAppUsers';
import { ContactPicker } from '@/components/common/ContactPicker';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { parseDateSafe } from '@/lib/timezoneUtils';

type MainView = 'list' | 'calendar';
type CalendarMode = 'month' | 'day';

function useDebounce<T>(value: T, delay: number) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  React.useEffect(() => {
    const h = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(h);
  }, [value, delay]);
  return debouncedValue;
}

const columnConfig: Record<string, { bgColor: string; borderColor: string; countBg: string; countText: string }> = {
  overdue: { bgColor: 'bg-rose-50 dark:bg-rose-900/30', borderColor: 'border-rose-100 dark:border-rose-800', countBg: 'bg-rose-100 dark:bg-rose-900', countText: 'text-rose-700 dark:text-rose-300' },
  today: { bgColor: 'bg-blue-50 dark:bg-blue-900/30', borderColor: 'border-blue-100 dark:border-blue-800', countBg: 'bg-blue-100 dark:bg-blue-900', countText: 'text-blue-700 dark:text-blue-300' },
  future: { bgColor: 'bg-slate-50 dark:bg-slate-800/50', borderColor: 'border-slate-100 dark:border-slate-700', countBg: 'bg-slate-100 dark:bg-slate-700', countText: 'text-slate-700 dark:text-slate-300' },
  completed: { bgColor: 'bg-green-50 dark:bg-green-900/30', borderColor: 'border-green-100 dark:border-green-800', countBg: 'bg-green-100 dark:bg-green-900', countText: 'text-green-700 dark:text-green-300' },
};

const KANBAN_COLUMNS = [
  { id: 'overdue', label: 'Atrasadas' },
  { id: 'today', label: 'Hoje' },
  { id: 'future', label: 'Futuras' },
  { id: 'completed', label: 'Concluídas' },
];

const TYPE_OPTIONS = [
  { value: 'call', label: 'Ligação' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'meeting', label: 'Reunião' },
  { value: 'email', label: 'Email' },
  { value: 'task', label: 'Tarefa' },
];

export default function Agenda() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const today = new Date();

  // ── View mode ──────────────────────────────────────────────────────────────
  const initialView = searchParams.get('view') === 'list' ? 'list' : 'calendar';
  const [view, setView] = useState<MainView>(initialView as MainView);
  const [calendarMode, setCalendarMode] = useState<CalendarMode>('month');
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [currentDay, setCurrentDay] = useState<Date>(today);

  const { data: currentUser } = useCurrentAppUser();
  const { users: allUsers } = useActiveAppUsersForSelect();
  const { users: fullUsers } = useAppUsers();

  const managerRoles = ['admin', 'manager', 'sales_director', 'sales_manager', 'it'];
  const isManager = currentUser?.system_role?.code
    ? managerRoles.includes(currentUser.system_role.code)
    : false;

  const [filterUserId, setFilterUserId] = useState<string | null>(null);
  const effectiveFilterUserId = filterUserId ?? (currentUser?.id ?? null);
  const isViewingOther = filterUserId !== null && filterUserId !== currentUser?.id;

  const [typeFilter, setTypeFilter] = useState<string[]>([]);
  const [showCompleted, setShowCompleted] = useState(false);
  const [contactFilter, setContactFilter] = useState<{ id: string; name: string } | null>(null);

  // ── Calendar queries ────────────────────────────────────────────────────────
  const queryYear = calendarMode === 'day' && view === 'calendar' ? currentDay.getFullYear() : year;
  const queryMonth = calendarMode === 'day' && view === 'calendar' ? currentDay.getMonth() + 1 : month;

  const { data: agendaEvents = [], isLoading: isLoadingEvents } = useAgendaEvents(
    queryYear,
    queryMonth,
    effectiveFilterUserId,
  );

  // Apply type filter to agenda events
  const filteredAgendaEvents = useMemo(() => {
    if (typeFilter.length === 0) return agendaEvents;
    return agendaEvents.filter((ev) => {
      const item = ev as AgendaItem;
      if (item._source === 'task') {
        const metaType = (item._raw?.metadata as any)?.type;
        return typeFilter.includes(metaType);
      }
      if (item._source === 'scheduled_call') return typeFilter.includes('call');
      if (item._source === 'calendar_event') {
        if (typeFilter.includes('meeting')) return item.type === 'meeting';
        return false;
      }
      return true;
    });
  }, [agendaEvents, typeFilter]);

  const dayEvents = useMemo(
    () => filteredAgendaEvents.filter((ev) => {
      const d = new Date(ev.start_at);
      return (
        d.getFullYear() === currentDay.getFullYear() &&
        d.getMonth() === currentDay.getMonth() &&
        d.getDate() === currentDay.getDate()
      );
    }),
    [filteredAgendaEvents, currentDay],
  );

  // ── Summary badges ─────────────────────────────────────────────────────────
  const summaryBadges = useMemo(() => {
    const todayStart = startOfDay(new Date());
    let overdue = 0;
    let todayCount = 0;
    let completed = 0;

    agendaEvents.forEach((ev) => {
      const item = ev as AgendaItem;
      if (item._source === 'task') {
        const raw = item._raw;
        if (raw?.status === 'completed') { completed++; return; }
        if (raw?.due_date) {
          const dueDate = parseDateSafe(raw.due_date);
          if (dueDate && isBefore(dueDate, todayStart)) overdue++;
          if (dueDate && isToday(dueDate)) todayCount++;
        }
      } else if (item._source === 'scheduled_call') {
        const raw = item._raw;
        if (raw?.status === 'completed') { completed++; return; }
        if (raw?.status === 'canceled') return;
        if (raw?.scheduled_for) {
          const callDate = startOfDay(new Date(raw.scheduled_for));
          if (isBefore(callDate, todayStart)) overdue++;
          else if (isToday(callDate)) todayCount++;
        }
      }
    });

    return { overdue, today: todayCount, completed };
  }, [agendaEvents]);

  // ── Tasks (list/kanban view) ────────────────────────────────────────────────
  const [searchQuery, setSearchQuery] = useState('');
  const [assignedFilter, setAssignedFilter] = useState<string | null>(null);
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const debouncedSearch = useDebounce(searchQuery, 300);

  React.useEffect(() => {
    if (currentUser?.id && assignedFilter === null) {
      setAssignedFilter(currentUser.id);
    }
  }, [currentUser?.id]);

  const rpcFilters = useMemo(() => ({
    assignedTo: assignedFilter === 'all' || assignedFilter === null ? null : assignedFilter,
    priority: priorityFilter === 'all' ? null : priorityFilter,
    search: debouncedSearch || null,
  }), [assignedFilter, priorityFilter, debouncedSearch]);

  const { tasks: rpcTasks, counts, isLoading: isLoadingTasks, updateTask, deleteTask } = useFilteredTasks(rpcFilters);

  // Busca adicional: tasks cujo CLIENTE bate com o termo digitado (escalações + contato direto + via deal)
  const tasksFromClientSearch = useEscalationTasksBySearch(debouncedSearch);

  // Quando o filtro "Cliente" está ativo, traz todas as tasks daquele cliente
  const tasksFromContactFilter = useTasksByClient({ contactId: contactFilter?.id ?? null });

  const tasks = useMemo(() => {
    const base = rpcTasks ?? [];
    const ids = new Set(base.map((t: any) => t.id));
    const merged: any[] = [...base];
    for (const t of tasksFromClientSearch ?? []) {
      if (!ids.has(t.id)) { ids.add(t.id); merged.push(t); }
    }
    for (const t of tasksFromContactFilter ?? []) {
      if (!ids.has(t.id)) { ids.add(t.id); merged.push(t); }
    }
    return merged;
  }, [rpcTasks, tasksFromClientSearch, tasksFromContactFilter]);

  // Mapa session_id -> contato, para tasks de Escalação CS exibidas na agenda
  const escalationSessionIds = useMemo(
    () => (tasks || [])
      .filter((t: any) => t?.metadata?.source === 'cs_escalation' && t?.metadata?.session_id)
      .map((t: any) => t.metadata.session_id as string),
    [tasks]
  );
  const escalationContacts = useEscalationContacts(escalationSessionIds);

  // ── Task detail sheet & escalation ─────────────────────────────────────────
  const [selectedTask, setSelectedTask] = useState<any>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [selectedCall, setSelectedCall] = useState<any>(null);
  const [isCallDetailOpen, setIsCallDetailOpen] = useState(false);
  const [escalationTask, setEscalationTask] = useState<any>(null);
  const [showEscalationModal, setShowEscalationModal] = useState(false);

  // Deep-link: abre a tarefa pelo ID da URL, mesmo que não esteja na lista do usuário
  const deepLinkTaskId = searchParams.get('taskId');
  const fromNotification = searchParams.get('fromNotification') === '1';
  const handledDeepLinkRef = React.useRef<string | null>(null);
  const [highlightResponse, setHighlightResponse] = useState(false);
  const { data: deepLinkTask, isFetched: deepLinkFetched, isError: deepLinkError } = useQuery({
    queryKey: ['task-by-id', deepLinkTaskId],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('tasks')
        .select('*, metadata')
        .eq('id', deepLinkTaskId)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!deepLinkTaskId,
  });

  React.useEffect(() => {
    if (!deepLinkTaskId || handledDeepLinkRef.current === deepLinkTaskId) return;
    const clear = () => {
      handledDeepLinkRef.current = deepLinkTaskId;
      const next = new URLSearchParams(searchParams);
      next.delete('taskId');
      next.delete('fromNotification');
      setSearchParams(next, { replace: true });
    };
    const found = tasks?.find((t: any) => t.id === deepLinkTaskId) ?? deepLinkTask;
    if (found) {
      setSelectedTask(found);
      setHighlightResponse(fromNotification);
      setIsDetailOpen(true);
      clear();
    } else if (deepLinkFetched || deepLinkError) {
      toast.error('Tarefa não encontrada');
      clear();
    }
  }, [deepLinkTaskId, fromNotification, tasks, deepLinkTask, deepLinkFetched, deepLinkError]);

  React.useEffect(() => {
    if (!deepLinkTaskId) handledDeepLinkRef.current = null;
  }, [deepLinkTaskId]);

  // ── Modal state ─────────────────────────────────────────────────────────────
  const [taskModalOpen, setTaskModalOpen] = useState(false);
  const [eventModalOpen, setEventModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [defaultDate, setDefaultDate] = useState<Date | null>(null);
  const [eventDefaultType, setEventDefaultType] = useState<'meeting' | 'reminder' | 'other' | 'unavailable' | undefined>(undefined);

  const openNew = (date?: Date) => {
    setDefaultDate(date ?? null);
    setTaskModalOpen(true);
  };

  const openEdit = (event: AgendaItem | CalendarEvent) => {
    const item = event as AgendaItem;
    if (item._source === 'task') {
      const raw = item._raw;
      setSelectedTask(raw);
      setIsDetailOpen(true);
    } else if (item._source === 'scheduled_call') {
      setSelectedCall(item._raw);
      setIsCallDetailOpen(true);
    } else {
      setSelectedEvent(event as CalendarEvent);
      setDefaultDate(null);
      setEventModalOpen(true);
    }
  };

  const closeEventModal = () => {
    setEventModalOpen(false);
    setSelectedEvent(null);
    setDefaultDate(null);
    setEventDefaultType(undefined);
  };

  // ── Navigation ──────────────────────────────────────────────────────────────
  const goToDayView = (date: Date) => { setCurrentDay(date); setCalendarMode('day'); };

  const prevPeriod = () => {
    if (view === 'calendar' && calendarMode === 'day') setCurrentDay((d) => subDays(d, 1));
    else {
      if (month === 1) { setYear((y) => y - 1); setMonth(12); }
      else setMonth((m) => m - 1);
    }
  };

  const nextPeriod = () => {
    if (view === 'calendar' && calendarMode === 'day') setCurrentDay((d) => addDays(d, 1));
    else {
      if (month === 12) { setYear((y) => y + 1); setMonth(1); }
      else setMonth((m) => m + 1);
    }
  };

  const goToday = () => {
    setCurrentDay(today);
    setYear(today.getFullYear());
    setMonth(today.getMonth() + 1);
  };

  // ── Drag & drop (kanban) ────────────────────────────────────────────────────
  const handleDragStart = (e: React.DragEvent, taskId: string) => {
    e.dataTransfer.setData('taskId', taskId);
  };

  const handleDrop = async (e: React.DragEvent, columnId: string) => {
    e.preventDefault();
    const taskId = e.dataTransfer.getData('taskId');
    try {
      if (columnId === 'completed') {
        await updateTask.mutateAsync({ id: taskId, status: 'completed', completed_at: new Date().toISOString() });
        return;
      }
      const todayStart = startOfDay(new Date());
      let newDueDate: string;
      if (columnId === 'today') {
        newDueDate = safeFormat(todayStart, 'yyyy-MM-dd');
      } else if (columnId === 'future') {
        newDueDate = safeFormat(addDays(todayStart, 1), 'yyyy-MM-dd');
      } else {
        newDueDate = safeFormat(subDays(todayStart, 1), 'yyyy-MM-dd');
      }
      await updateTask.mutateAsync({ id: taskId, due_date: newDueDate });
    } catch (err) {
      console.error('Error updating task:', err);
    }
  };

  const handleKommoReviewAction = async (task: any, action: string) => {
    try {
      if (action === 'belongs') {
        await updateTask.mutateAsync({ id: task.id, status: 'completed', completed_at: new Date().toISOString() });
        toast.success('Verificação concluída - case pertence ao deal');
      } else {
        const metadata = task.metadata;
        const { data: originalDeal, error: dealError } = await (supabase as any)
          .from('deals').select('contact_id, name').eq('id', metadata.original_deal_id).single();
        if (dealError) throw dealError;
        const { data: newDeal, error: newDealError } = await (supabase as any)
          .from('deals').insert({ contact_id: originalDeal.contact_id, source: 'direct', stage: 'WON', name: `Import Kommo - Separado de ${originalDeal.name}`, notes: 'Deal criado após verificação de importação Kommo' }).select('id').single();
        if (newDealError) throw newDealError;
        const { error: updateError } = await (supabase as any).from('items').update({ deal_id: newDeal.id }).eq('id', metadata.item_id);
        if (updateError) throw updateError;
        await updateTask.mutateAsync({ id: task.id, status: 'completed', completed_at: new Date().toISOString() });
        toast.success('Novo deal criado e case revinculado');
      }
    } catch (err: any) {
      toast.error('Erro: ' + err.message);
    }
  };

  const handleCompleteFromSheet = async (task: any) => {
    if (task.metadata?.source === 'cs_escalation') {
      setEscalationTask(task);
      setShowEscalationModal(true);
      setIsDetailOpen(false);
    } else {
      await updateTask.mutateAsync({ id: task.id, status: 'completed', completed_at: new Date().toISOString() });
      queryClient.invalidateQueries({ queryKey: ['agenda-events'] });
    }
  };

  const filteredTasks = useMemo(() => {
    let list = tasks ?? [];
    if (typeFilter.length > 0) {
      list = list.filter((t: any) => {
        const effectiveType = (t.metadata as any)?.type || 'task';
        return typeFilter.includes(effectiveType);
      });
    }
    if (contactFilter?.id) {
      const cid = contactFilter.id;
      const escMatchIds = new Set(
        Object.entries(escalationContacts || {})
          .filter(([, c]: any) => c?.contactId === cid)
          .map(([sid]) => sid)
      );
      list = list.filter((t: any) => {
        if (t.contact_id === cid) return true;
        if (t.deals?.contact_id === cid) return true;
        const sid = t?.metadata?.session_id;
        if (sid && escMatchIds.has(sid)) return true;
        return false;
      });
    }
    return list;
  }, [tasks, typeFilter, contactFilter, escalationContacts]);

  // Extract scheduled calls from agenda events for the kanban view
  const scheduledCallsForKanban = useMemo(() => {
    return filteredAgendaEvents
      .filter((ev) => (ev as AgendaItem)._source === 'scheduled_call')
      .map((ev) => {
        const item = ev as AgendaItem;
        const raw = item._raw;
        return {
          ...raw,
          _isCall: true,
          _agendaItem: item,
        };
      });
  }, [filteredAgendaEvents]);

  const getTasksByTimeGroup = useCallback((group: string) => {
    const todayStart = startOfDay(new Date());

    // Filter tasks
    const taskItems = filteredTasks.filter((t: any) => {
      if (group === 'completed') return t.status === 'completed';
      if (t.status === 'completed') return false;
      const dueDate = t.due_date ? startOfDay(parseDateSafe(t.due_date) ?? new Date()) : null;
      if (group === 'overdue') return dueDate && isBefore(dueDate, todayStart);
      if (group === 'today') return !dueDate || isSameDay(dueDate, todayStart);
      return dueDate && isAfter(dueDate, todayStart);
    });

    // Filter scheduled calls
    const callItems = scheduledCallsForKanban.filter((c: any) => {
      if (group === 'completed') return c.status === 'completed';
      if (c.status === 'completed' || c.status === 'canceled') return false;
      if (!c.scheduled_for) return group === 'today';
      const callDate = startOfDay(new Date(c.scheduled_for));
      if (group === 'overdue') return isBefore(callDate, todayStart);
      if (group === 'today') return isSameDay(callDate, todayStart);
      return isAfter(callDate, todayStart);
    });

    return [...taskItems, ...callItems];
  }, [filteredTasks, scheduledCallsForKanban]);

  const getPriorityColor = (priority: string) => {
    if (priority === 'urgent' || priority === 'high') return 'destructive';
    if (priority === 'medium') return 'secondary';
    return 'outline';
  };

  const getPriorityLabel = (priority: string) => {
    const map: Record<string, string> = { low: 'Baixa', medium: 'Média', high: 'Alta', urgent: 'Urgente' };
    return map[priority] ?? priority;
  };

  // ── Labels ──────────────────────────────────────────────────────────────────
  const periodLabel = view === 'calendar' && calendarMode === 'day'
    ? safeFormat(currentDay, "EEE, d 'de' MMMM", { locale: ptBR }).replace(/^\w/, (c) => c.toUpperCase())
    : (() => {
        const s = safeFormat(new Date(year, month - 1, 1), 'MMMM yyyy', { locale: ptBR });
        return s.charAt(0).toUpperCase() + s.slice(1);
      })();

  const getUserName = (u: { first_name: string | null; last_name: string | null; display_name: string | null }) =>
    u.display_name || [u.first_name, u.last_name].filter(Boolean).join(' ') || '—';

  const hasActiveFilters = (assignedFilter && assignedFilter !== currentUser?.id) || priorityFilter !== 'all' || filterUserId || typeFilter.length > 0 || !!contactFilter;

  // ── Draft filters for modal ────────────────────────────────────────────────
  interface DraftFilters {
    typeFilter: string[];
    filterUserId: string | null;
    assignedFilter: string | null;
    priorityFilter: string;
    showCompleted: boolean;
    contactFilter: { id: string; name: string } | null;
  }
  const [draftFilters, setDraftFilters] = useState<DraftFilters | null>(null);

  const openFilterModal = () => {
    setDraftFilters({
      typeFilter: [...typeFilter],
      filterUserId,
      assignedFilter,
      priorityFilter,
      showCompleted,
      contactFilter,
    });
    setIsFilterOpen(true);
  };

  const clearDraftFilters = () => {
    setDraftFilters({
      typeFilter: [],
      filterUserId: null,
      assignedFilter: null,
      priorityFilter: 'all',
      showCompleted: false,
      contactFilter: null,
    });
  };

  const applyFilters = () => {
    if (draftFilters) {
      setTypeFilter(draftFilters.typeFilter);
      setFilterUserId(draftFilters.filterUserId);
      setAssignedFilter(draftFilters.assignedFilter);
      setPriorityFilter(draftFilters.priorityFilter);
      setShowCompleted(draftFilters.showCompleted);
      setContactFilter(draftFilters.contactFilter);
    }
    setIsFilterOpen(false);
    setDraftFilters(null);
  };

  const closeFilterModal = (open: boolean) => {
    if (!open) {
      setDraftFilters(null);
    }
    setIsFilterOpen(open);
  };

  return (
    <div className="p-4 md:p-6 flex-1 flex flex-col h-full space-y-3">
      {/* ── ROW 1: Title + Summary Badges + View Toggle + New Button ──── */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <h1 className="text-2xl font-bold whitespace-nowrap">Tarefas</h1>
          <div className="hidden sm:flex items-center gap-2">
            {summaryBadges.overdue > 0 && (
              <span className="inline-flex items-center gap-1.5 text-xs font-medium text-rose-700 dark:text-rose-400 bg-rose-50 dark:bg-rose-900/30 px-2.5 py-1 rounded-full">
                <span className="w-1.5 h-1.5 rounded-full bg-rose-500" />
                {summaryBadges.overdue} atrasadas
              </span>
            )}
            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-700 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 px-2.5 py-1 rounded-full">
              <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
              {summaryBadges.today} hoje
            </span>
            <button
              onClick={() => setShowCompleted(!showCompleted)}
              className={cn(
                'inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full transition-colors',
                showCompleted
                  ? 'text-emerald-700 dark:text-emerald-400 bg-emerald-100 dark:bg-emerald-900/50 ring-1 ring-emerald-300 dark:ring-emerald-700'
                  : 'text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/30 hover:bg-emerald-100 dark:hover:bg-emerald-900/50'
              )}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
              {summaryBadges.completed} concluídas
              {showCompleted ? <X className="w-3 h-3 ml-0.5" /> : <Check className="w-3 h-3 ml-0.5 opacity-50" />}
            </button>
          </div>
        </div>

        {/* Actions: calendar controls, + button, view toggle, search, filter */}
        <div className="flex items-center gap-2">
          {view === 'calendar' && (
            <>
              {/* Sub-tabs Mês/Dia */}
              <div className="flex border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden h-8">
                <button
                  onClick={() => setCalendarMode('month')}
                  className={cn(
                    'px-3 text-xs font-medium transition-colors',
                    calendarMode === 'month'
                      ? 'bg-secondary text-secondary-foreground'
                      : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
                  )}
                >
                  Mês
                </button>
                <button
                  onClick={() => { setCurrentDay(today); setCalendarMode('day'); }}
                  className={cn(
                    'px-3 text-xs font-medium transition-colors border-l border-slate-200 dark:border-slate-700',
                    calendarMode === 'day'
                      ? 'bg-secondary text-secondary-foreground'
                      : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
                  )}
                >
                  Dia
                </button>
              </div>

              {/* Date navigation */}
              <div className="flex items-center gap-1 flex-shrink-0">
                <Button variant="ghost" size="sm" className="text-xs text-muted-foreground h-7 px-2" onClick={goToday}>
                  Hoje
                </Button>
                <Button variant="ghost" size="icon" className="h-7 w-7 rounded-full" onClick={prevPeriod}>
                  <ChevronLeft className="w-3.5 h-3.5" />
                </Button>
                <span className="text-sm font-medium min-w-[120px] text-center whitespace-nowrap">{periodLabel}</span>
                <Button variant="ghost" size="icon" className="h-7 w-7 rounded-full" onClick={nextPeriod}>
                  <ChevronRight className="w-3.5 h-3.5" />
                </Button>
              </div>
            </>
          )}

          {!isViewingOther && (
            <Button
              onClick={() => openNew(view === 'calendar' && calendarMode === 'day' ? currentDay : undefined)}
              size="icon"
              className="h-9 w-9 rounded-full"
            >
              <Plus className="w-4 h-4" />
            </Button>
          )}

          <div className="flex border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden h-9">
            <Button
              variant={view === 'list' ? 'secondary' : 'ghost'}
              size="sm"
              className="rounded-none h-full px-3 text-xs"
              onClick={() => setView('list')}
            >
              <ListChecks className="w-3.5 h-3.5 mr-1" />
              Lista
            </Button>
            <Button
              variant={view === 'calendar' ? 'secondary' : 'ghost'}
              size="sm"
              className="rounded-none h-full px-3 text-xs border-l border-slate-200 dark:border-slate-700"
              onClick={() => setView('calendar')}
            >
              <CalendarDays className="w-3.5 h-3.5 mr-1" />
              Calendário
            </Button>
          </div>

          {view === 'list' && (
            <Button
              variant="ghost"
              size="icon"
              className={cn('rounded-full h-9 w-9 flex-shrink-0', showSearch && 'bg-primary text-primary-foreground')}
              onClick={() => { setShowSearch(!showSearch); if (showSearch) setSearchQuery(''); }}
            >
              <Search className="w-4 h-4" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            className={cn('rounded-full h-9 w-9 flex-shrink-0', hasActiveFilters && 'bg-primary text-primary-foreground')}
            onClick={openFilterModal}
          >
            <Filter className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* ── Search bar (expandable) ───────────────────────────────────── */}
      {showSearch && (
        <div className="mb-1">
          <SearchBar
            value={searchQuery}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
            placeholder="Buscar por título, descrição ou cliente..."
            autoFocus
          />
        </div>
      )}

      {/* ── Filter modal (global) ─────────────────────────────────────── */}
      <ResponsiveDialog open={isFilterOpen} onOpenChange={closeFilterModal} title="Filtros">
        <div className="flex flex-col gap-4 py-2">
          {/* Tipo — MultiSelect */}
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-muted-foreground">Tipo</label>
            <MultiSelect
              options={TYPE_OPTIONS}
              value={draftFilters?.typeFilter ?? typeFilter}
              onChange={(val) => setDraftFilters((d) => d ? { ...d, typeFilter: val } : d)}
              placeholder="Todos os tipos"
              emptyText="Nenhum tipo encontrado."
            />
          </div>

          {view === 'calendar' && allUsers.length > 0 && (
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-muted-foreground">Agenda de</label>
              <Select
                value={draftFilters?.filterUserId ?? filterUserId ?? '__mine__'}
                onValueChange={(v) => setDraftFilters((d) => d ? { ...d, filterUserId: v === '__mine__' ? null : v } : d)}
              >
                <SelectTrigger className="w-full h-9 rounded-full">
                  <User className="h-4 w-4 mr-2 text-muted-foreground" />
                  <SelectValue placeholder="Minha Agenda" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__mine__">Minha Agenda</SelectItem>
                  {isManager && <SelectItem value="__all__">Todos os usuários</SelectItem>}
                  {allUsers
                    .filter((u) => u.id !== currentUser?.id)
                    .filter((u, idx, arr) => arr.findIndex((x) => getUserName(x) === getUserName(u)) === idx)
                    .map((u) => (
                      <SelectItem key={u.id} value={u.id}>{getUserName(u)}</SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {view === 'list' && (
            <>
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-muted-foreground">Responsável</label>
                <Select
                  value={draftFilters?.assignedFilter ?? assignedFilter ?? 'all'}
                  onValueChange={(v) => setDraftFilters((d) => d ? { ...d, assignedFilter: v === 'all' ? null : v } : d)}
                >
                  <SelectTrigger className="w-full h-9 rounded-full">
                    <User className="h-4 w-4 mr-2 text-muted-foreground" />
                    <SelectValue placeholder="Responsável" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    {currentUser && <SelectItem value={currentUser.id}>Minhas Tarefas</SelectItem>}
                    {fullUsers.filter((u: any) => u.id !== currentUser?.id && u.active).map((u: any) => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.display_name || [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <label className="text-sm font-medium text-muted-foreground">Prioridade</label>
                <Select
                  value={draftFilters?.priorityFilter ?? priorityFilter}
                  onValueChange={(v) => setDraftFilters((d) => d ? { ...d, priorityFilter: v } : d)}
                >
                  <SelectTrigger className="w-full h-9 rounded-full">
                    <AlertCircle className="h-4 w-4 mr-2 text-muted-foreground" />
                    <SelectValue placeholder="Prioridade" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas</SelectItem>
                    <SelectItem value="urgent">Urgente</SelectItem>
                    <SelectItem value="high">Alta</SelectItem>
                    <SelectItem value="medium">Média</SelectItem>
                    <SelectItem value="low">Baixa</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </>
          )}


          <div className="space-y-1.5">
            <label className="text-sm font-medium text-muted-foreground">Cliente</label>
            <ContactPicker
              value={draftFilters ? draftFilters.contactFilter : contactFilter}
              onChange={(val) => setDraftFilters((d) => d ? { ...d, contactFilter: val } : d)}
              placeholder="Buscar cliente..."
            />
          </div>


          <div className="flex items-center justify-between py-2 border-t border-border">
            <Label htmlFor="show-completed-filter" className="text-sm font-medium text-muted-foreground cursor-pointer">Mostrar concluídas</Label>
            <Switch
              id="show-completed-filter"
              checked={draftFilters?.showCompleted ?? showCompleted}
              onCheckedChange={(val) => setDraftFilters((d) => d ? { ...d, showCompleted: val } : d)}
            />
          </div>

          <div className="flex gap-2 mt-2">
            <Button variant="outline" className="flex-1" onClick={clearDraftFilters}>
              Limpar
            </Button>
            <Button className="flex-1" onClick={applyFilters}>
              Aplicar
            </Button>
          </div>
        </div>
      </ResponsiveDialog>

      {/* ── LIST / KANBAN VIEW ─────────────────────────────────────────── */}
      {view === 'list' && (
        <div className="flex flex-col flex-1 overflow-hidden -mx-4 md:-mx-6 px-4 md:px-6">
          {(isLoadingTasks || isLoadingEvents) ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : (
            <div className="flex gap-3 overflow-x-auto pb-4 flex-1 overflow-y-hidden">
              {KANBAN_COLUMNS.filter(col => col.id !== 'completed' || showCompleted).map((column) => {
                const config = columnConfig[column.id] ?? columnConfig.today;
                const colTasks = getTasksByTimeGroup(column.id);
                return (
                  <div
                    key={column.id}
                    className="flex flex-col flex-1 min-w-[260px] h-full"
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => handleDrop(e, column.id)}
                  >
                    <div className={`${config.bgColor} ${config.borderColor} border rounded-t-lg px-4 py-3 flex-shrink-0`}>
                      <div className="flex items-center justify-between gap-2">
                        <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100 truncate">{column.label}</h3>
                        <span className={`${config.countBg} ${config.countText} px-2.5 py-0.5 rounded-full text-xs font-medium`}>
                          {colTasks.length}
                        </span>
                      </div>
                    </div>
                    <div className={`${config.bgColor} ${config.borderColor} border-x border-b rounded-b-lg flex-1 overflow-y-auto scrollbar-hide space-y-3 p-3`}>
                      {colTasks.map((item: any) => {
                        const isCall = !!item._isCall;
                        return (
                        <Card
                          key={isCall ? `call_${item.id}` : item.id}
                          draggable={!isCall}
                          onDragStart={(e) => !isCall && handleDragStart(e, item.id)}
                          onClick={() => {
                            if (isCall) {
                              setSelectedCall(item);
                              setIsCallDetailOpen(true);
                            } else {
                              setSelectedTask(item);
                              setIsDetailOpen(true);
                            }
                          }}
                          className={cn('cursor-pointer transition-all duration-200 hover:shadow-lg hover:border-primary/50 active:scale-[0.98]', isCall && 'border-l-2 border-l-emerald-500')}
                        >
                          <CardContent className="p-4">
                            <div className="space-y-3">
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex items-center gap-1.5">
                                  {isCall && <Phone className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" />}
                                  <h4 className="font-medium leading-tight">{item.title || 'Ligação agendada'}</h4>
                                </div>
                                {!isCall && item.priority && (
                                  <Badge variant={getPriorityColor(item.priority)} className="text-xs">
                                    {getPriorityLabel(item.priority)}
                                  </Badge>
                                )}
                                {isCall && (
                                  <Badge variant="outline" className="text-xs text-emerald-700 border-emerald-300 bg-emerald-50">
                                    Ligação
                                  </Badge>
                                )}
                              </div>
                              {!isCall && item.description && (
                                <p className="text-sm text-muted-foreground line-clamp-2">{item.description}</p>
                              )}
                              {isCall && item.to_e164 && (
                                <p className="text-sm text-muted-foreground">{item.to_e164}</p>
                              )}
                              <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                                {!isCall && item.due_date && (
                                  <div className="flex items-center gap-1">
                                    <Clock className="w-3 h-3" />
                                    {parseDateSafe(item.due_date)?.toLocaleDateString('pt-BR') || ''}
                                  </div>
                                )}
                                {isCall && item.scheduled_for && (
                                  <div className="flex items-center gap-1">
                                    <Clock className="w-3 h-3" />
                                    {item._raw?.metadata?.all_day
                                      ? safeFormat(parseDateSafe(item.scheduled_for?.slice(0, 10)) ?? new Date(), 'dd/MM')
                                      : new Date(item.scheduled_for).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                                  </div>
                                )}
                                {!isCall && item.assigned_to && (
                                  <div className="flex items-center gap-1">
                                    <User className="w-3 h-3" />
                                    {item.assigned_user_name || fullUsers?.find((u: any) => u.id === item.assigned_to)?.display_name || 'Atribuído'}
                                  </div>
                                )}
                              </div>
                              {!isCall && item.metadata?.source === 'cs_escalation' && (
                                <div className="flex flex-col gap-1.5 pt-2 border-t border-border">
                                  <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200 text-xs w-fit">
                                    🔺 Escalação CS
                                  </Badge>
                                  {item.metadata?.session_id && (
                                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                      <User className="w-3 h-3" />
                                      <span className="font-medium text-foreground">
                                        {escalationContacts[item.metadata.session_id]?.contactName || 'Cliente não identificado'}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              )}
                              {!isCall && item.metadata?.type === 'kommo_review' && item.status === 'pending' && (
                                <div className="flex gap-2 mt-3 pt-3 border-t border-border">
                                  <Button
                                    size="sm" variant="outline"
                                    className="flex-1 text-emerald-600 border-emerald-600 hover:bg-emerald-50"
                                    onClick={(e) => { e.stopPropagation(); handleKommoReviewAction(item, 'belongs'); }}
                                  >
                                    <Check className="w-3 h-3 mr-1" /> Pertence
                                  </Button>
                                  <Button
                                    size="sm" variant="outline"
                                    className="flex-1 text-red-600 border-red-600 hover:bg-red-50"
                                    onClick={(e) => { e.stopPropagation(); handleKommoReviewAction(item, 'not_belongs'); }}
                                  >
                                    <X className="w-3 h-3 mr-1" /> Não Pertence
                                  </Button>
                                </div>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                        );
                      })}
                      {colTasks.length === 0 && (
                        <div className="text-center py-8 text-muted-foreground text-sm">{t('tasks.noTasksInColumn')}</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── CALENDAR VIEWS ────────────────────────────────────────────── */}
      {view === 'calendar' && (
        isLoadingEvents ? (
          <div className="text-center py-16 text-muted-foreground">Carregando eventos…</div>
        ) : calendarMode === 'month' ? (
          <CalendarGrid
            year={year}
            month={month}
            events={filteredAgendaEvents}
            onDayClick={openNew}
            onDayLabelClick={goToDayView}
            onEventClick={openEdit}
          />
        ) : (
          <DayView
            date={currentDay}
            events={dayEvents}
            onEventClick={openEdit}
            onTimeSlotClick={openNew}
          />
        )
      )}

      {/* Task create modal */}
      <TaskFormModal
        open={taskModalOpen}
        onClose={() => setTaskModalOpen(false)}
        defaultDate={defaultDate}
        onOpenVideoMeeting={(date) => {
          setDefaultDate(date ?? null);
          setEventDefaultType('meeting');
          setEventModalOpen(true);
        }}
      />

      {/* Calendar event create/edit modal (Daily.co flow) */}
      <EventFormModal
        open={eventModalOpen}
        onClose={closeEventModal}
        event={selectedEvent}
        defaultDate={defaultDate}
        readOnly={isViewingOther}
        defaultType={eventDefaultType}
      />

      {/* Task detail sheet */}
      <TaskDetailSheet
        task={selectedTask}
        open={isDetailOpen}
        highlightResponse={highlightResponse}
        onOpenChange={(open) => { setIsDetailOpen(open); if (!open) { setSelectedTask(null); setHighlightResponse(false); } }}
        users={fullUsers}
        onUpdate={async (id: string, data: any) => { await updateTask.mutateAsync({ id, ...data }); queryClient.invalidateQueries({ queryKey: ['agenda-events'] }); }}
        onDelete={async (id: string) => { await deleteTask.mutateAsync(id); queryClient.invalidateQueries({ queryKey: ['agenda-events'] }); setIsDetailOpen(false); setSelectedTask(null); }}
        onComplete={handleCompleteFromSheet}
        isUpdating={updateTask.isPending}
        isDeleting={deleteTask.isPending}
      />

      {/* Call detail sheet */}
      <CallDetailSheet
        call={selectedCall}
        open={isCallDetailOpen}
        onOpenChange={(open) => { setIsCallDetailOpen(open); if (!open) setSelectedCall(null); }}
      />


      <EscalationTaskCompletionModal
        open={showEscalationModal}
        onClose={() => { setShowEscalationModal(false); setEscalationTask(null); }}
        task={escalationTask}
        contactName={escalationTask?.metadata?.session_id ? escalationContacts[escalationTask.metadata.session_id]?.contactName : null}
        onComplete={async (taskId: string, response: string) => {
          await updateTask.mutateAsync({ id: taskId, notes: response, status: 'completed', completed_at: new Date().toISOString() });
          queryClient.invalidateQueries({ queryKey: ['agenda-events'] });
          setShowEscalationModal(false);
          setEscalationTask(null);
        }}
        isSubmitting={updateTask.isPending}
      />
    </div>
  );
}
